import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProfessionalsActions } from 'application/constants/actions';
import { ProfessionalsAPI } from 'integration/api/professionals';
import { fetchProfessionalsProps } from 'common/types/professionals';
import { AxiosError } from 'axios';
import { LoginAsDto } from '@docbay/schemas';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetches professionals based on the provided parameters
 *
 * @param params - The parameters to fetch professionals with
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const fetchProfessionals = createAsyncThunk(
  ProfessionalsActions.fetchProfessionals,
  async (params: fetchProfessionalsProps, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchProfessionals(params);

      await delay(1000);

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Logs in a user with the provided credentials
 *
 * @param params - The login credentials to use
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const loginAsUser = createAsyncThunk(
  ProfessionalsActions.loginAsUser,
  async (params: LoginAsDto, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.loginAsUser(params);

      await delay(1000);

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a clinic owner with the provided ID
 *
 * @param id - The ID of the clinic owner to delete
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const deleteClinicOwner = createAsyncThunk(
  ProfessionalsActions.deleteClinicOwner,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.deleteClinicOwner(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a doctor with the provided ID
 *
 * @param id - The ID of the doctor to delete
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const deleteDoctor = createAsyncThunk(
  ProfessionalsActions.deleteDoctor,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.deleteDoctor(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a secretary with the provided ID
 *
 * @param id - The ID of the secretary to delete
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const deleteSecretary = createAsyncThunk(
  ProfessionalsActions.deleteSecretary,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.deleteSecretary(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an admin with the provided ID
 *
 * @param id - The ID of the admin to delete
 * @returns The data retrieved from the API call or an error message if it fails
 */
export const deleteAdmin = createAsyncThunk(
  ProfessionalsActions.deleteAdmin,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.deleteAdmin(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
